import { useEffect, useState, useCallback, type KeyboardEvent } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Search,
  Loader2,
  ThumbsUp,
  ThumbsDown,
  ExternalLink,
} from 'lucide-react'
import { queryKnowledgeBase } from '@/lib/supportApi'
import type { KBArticleRef } from '@/lib/supportApi'
import { TicketForm } from '@/components/support/TicketForm'
import { SupportChatPopup } from '@/components/support/SupportChatPopup'
import { PublicHeader } from '@/components/PublicHeader'

export default function SearchResults() {
  const [searchParams, setSearchParams] = useSearchParams()
  const queryParam = searchParams.get('query') ?? ''

  const [inputValue, setInputValue] = useState(queryParam)
  const [isLoading, setIsLoading] = useState(false)
  const [answer, setAnswer] = useState<string | null>(null)
  const [hasAnswer, setHasAnswer] = useState(false)
  const [articles, setArticles] = useState<KBArticleRef[]>([])
  const [feedback, setFeedback] = useState<'positive' | 'negative' | null>(null)
  const [searchedQuery, setSearchedQuery] = useState('')

  const doSearch = useCallback(async (q: string) => {
    if (!q.trim()) return

    setIsLoading(true)
    setAnswer(null)
    setArticles([])
    setHasAnswer(false)
    setFeedback(null)
    setSearchedQuery(q.trim())

    try {
      const result = await queryKnowledgeBase(q.trim())
      setAnswer(result.answer || 'Não encontrei informações sobre esse assunto na base de conhecimento.')
      setHasAnswer(result.hasAnswer)
      setArticles(result.articles)
    } catch {
      setAnswer('Ocorreu um erro ao processar sua pergunta. Tente novamente.')
      setHasAnswer(false)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    if (queryParam) {
      setInputValue(queryParam)
      doSearch(queryParam)
    }
  }, [queryParam, doSearch])

  const handleSearch = () => {
    const q = inputValue.trim()
    if (!q) return
    setSearchParams({ query: q })
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSearch()
    }
  }

  return (
    <div
      className="min-h-screen flex flex-col bg-white text-[#3c3939] antialiased"
      style={{ fontFamily: 'Montserrat, system-ui, sans-serif' }}
    >
      <PublicHeader />

      {/* Main content */}
      <main className="flex-1">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm mb-6">
            <Link to="/" className="text-blue-600 hover:underline">
              Página Inicial
            </Link>
            <span className="text-gray-400">/</span>
            <span className="text-gray-600">Resultados da Pesquisa</span>
          </nav>

          <hr className="border-gray-100 mb-6" />

          {/* Campo de busca */}
          <div className="relative mb-8">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="search"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Buscar na central de ajuda..."
              className="w-full pl-12 pr-4 py-3.5 rounded-lg border border-gray-200 bg-gray-50 text-base text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-all"
              disabled={isLoading}
            />
          </div>

          {/* Loading */}
          {isLoading && (
            <div className="flex items-center gap-3 py-16 justify-center text-gray-500">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-sm">Buscando na base de conhecimento...</span>
            </div>
          )}

          {/* Resultados */}
          <AnimatePresence mode="wait">
            {!isLoading && answer && (
              <motion.div
                key="results"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
              >
                {/* Heading */}
                <div className="mb-2">
                  <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">
                    Resultados para "<strong>{searchedQuery}</strong>"
                  </h1>
                </div>

                {/* Subtitle — fundo cinza claro */}
                <div className="bg-gray-50 rounded-lg px-5 py-4 mb-8">
                  <p className="text-sm text-gray-600">
                    Resposta gerada por inteligência artificial.
                  </p>
                  <p className="text-sm text-gray-500">
                    Deixe sua opinião abaixo e nos ajude a aperfeiçoar essa ferramenta.
                  </p>
                </div>

                {/* Resposta da IA */}
                <div className="mb-8">
                  <div className="text-[15px] text-gray-800 leading-relaxed whitespace-pre-wrap">
                    {answer}
                  </div>
                </div>

                {/* Feedback — só aparece quando tem resposta */}
                {hasAnswer && (
                  <div className="border-t border-gray-100 pt-6 mb-10">
                    <p className="text-sm font-semibold text-gray-800 mb-1">
                      Essa informação te ajudou?
                    </p>
                    <p className="text-xs text-gray-500 mb-4">
                      Nos ajude a evoluir e entre em contato se precisar de mais assistência.
                    </p>
                    <div className="flex flex-wrap gap-3">
                      <button
                        onClick={() => setFeedback('positive')}
                        className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full border text-sm font-medium transition-all ${
                          feedback === 'positive'
                            ? 'border-green-500 bg-green-50 text-green-700'
                            : 'border-green-400 text-green-600 hover:bg-green-50'
                        }`}
                      >
                        <ThumbsUp className="h-4 w-4" />
                        A resposta me ajudou
                      </button>
                      <button
                        onClick={() => setFeedback('negative')}
                        className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full border text-sm font-medium transition-all ${
                          feedback === 'negative'
                            ? 'border-red-500 bg-red-50 text-red-700'
                            : 'border-red-400 text-red-600 hover:bg-red-50'
                        }`}
                      >
                        <ThumbsDown className="h-4 w-4" />
                        A resposta não me ajudou
                      </button>
                    </div>

                    {/* Ticket form — aparece ao clicar "não me ajudou" */}
                    {feedback === 'negative' && (
                      <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mt-6"
                      >
                        <TicketForm originalQuestion={searchedQuery} />
                      </motion.div>
                    )}
                  </div>
                )}

                {/* Artigos recomendados */}
                {articles.length > 0 && (
                  <div className="mb-10">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">
                      Artigos recomendados
                    </h2>

                    <div className="divide-y divide-gray-100">
                      {articles.map((article) => {
                        const articleHref = article.sourceUrl ?? `/artigo/${article.id}`
                        return (
                          <a
                            key={article.id}
                            href={articleHref}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center gap-3 py-4 group"
                          >
                            <span className="text-sm font-medium text-green-600 group-hover:text-green-700 group-hover:underline flex-1">
                              {article.title}
                            </span>
                            <ExternalLink className="h-3.5 w-3.5 text-gray-300 group-hover:text-green-500 shrink-0 transition-colors" />
                          </a>
                        )
                      })}
                    </div>
                  </div>
                )}

                {/* Ticket form — quando não encontrou */}
                {!hasAnswer && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="mb-10"
                  >
                    <TicketForm originalQuestion={searchedQuery} />
                  </motion.div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-100 py-6 text-center text-xs text-gray-400">
        <div className="max-w-4xl mx-auto px-4">
          &copy; {new Date().getFullYear()} RTG &mdash; Central de Ajuda
        </div>
      </footer>

      <SupportChatPopup />
    </div>
  )
}
